/**
 * URL Generator for Embed Configuration
 *
 * Generates embed URLs with configuration parameters encoded in the query string.
 * All configuration is stored in URL parameters - no database required.
 */

/**
 * Configuration for a single product within a multi-product embed.
 * Each entry in the `products` array of EmbedConfig uses this shape.
 */
export interface EmbedProductConfig {
  productTag: string;
  shopifyVariantId?: string;
  shopifyDomain?: string;
  productTitle?: string;
  productPriceMin?: string;
  productPriceMax?: string;
  productCurrency?: string;
  productCompareAtPriceMin?: string;
  allowedStyles?: string[];
  allowedColors?: string[];
  allowedFonts?: string[];
  allowedIcons?: string[];
  allowedMaterials?: string[];
  isDefault?: boolean;
}

export interface EmbedConfig {
  productTag: string;
  partnerKey?: string;
  partnerId?: string;
  shopifyVariantId?: string;
  shopifyDomain?: string;
  productTitle?: string;
  productPriceMin?: string;
  productPriceMax?: string;
  productCurrency?: string;
  productCompareAtPriceMin?: string;
  allowedStyles?: string[];
  allowedColors?: string[];
  allowedFonts?: string[];
  allowedIcons?: string[];
  allowedMaterials?: string[];
  width?: string;
  height?: string;
  // Appearance customization (Phase 1 & 2)
  fontFamily?: string; // Google Font name e.g. "Inter", or "custom"
  customFontClass?: string; // CSS class applied to root element when fontFamily = "custom"
  // Appearance customization (Phase 3)
  brandBannerColor?: string; // hex, maps to brand banner background
  accentColor?: string; // hex, maps to active/selection indicators
  primaryButtonColor?: string; // hex, maps to primary filled buttons
  primaryButtonText?: string; // hex, maps to primary filled button text
  secondaryButtonColor?: string; // hex, maps to secondary button background
  secondaryButtonText?: string; // hex, maps to secondary button text + border
  headerBgColor?: string; // hex, maps to header_bg_color
  bodyBgColor?: string; // hex, maps to body_bg_color
  borderColor?: string; // hex, maps to border_color
  textColor?: string; // hex, maps to text_color → --nb-text
  secondaryTextColor?: string; // hex, maps to secondary/inactive helper text
  brandName?: string; // shown above product title as "Brand x NameBubbles"
  affiliateRfsn?: string; // full Refersion tracking code e.g. "9011032.2d9ff2"
  // Multi-product support: when 2+ products are provided they are base64-encoded
  // into the `products` URL fragment param (see generateEmbedUrl). Single-product
  // configs use the flat params above.
  products?: EmbedProductConfig[];
  // Dashboard-only hint: overrides the initial active product shown in the live
  // preview iframe. Never included in the copy-able embed code snippet.
  previewProductTag?: string;
}

interface SnippetOptions {
  mobileAutoResize?: boolean;
}

function buildEmbedUrlForSnippet(
  embedUrl: string,
  options?: SnippetOptions
): string {
  if (!options?.mobileAutoResize) return embedUrl;

  const url = new URL(embedUrl);
  url.searchParams.set('auto_resize', '1');
  return url.toString();
}

/**
 * Generates an embed URL from a configuration object
 *
 * @param baseUrl - The base URL for the embed widget (e.g., "https://namebubbles.com/embed-config.html")
 * @param config - The embed configuration object
 * @returns The complete embed URL with query parameters
 *
 * @example
 * const url = generateEmbedUrl("https://namebubbles.com/embed-config.html", {
 *   productTag: "product-design:36wKmPuxaq3q6hfV7RqlhMHejov",
 *   partnerKey: "affiliate-123",
 *   allowedStyles: ["1", "2"],
 *   allowedColors: ["red", "blue"]
 * });
 * // Returns: https://namebubbles.com/embed-config.html?product_tag=product-design%3A36wKmPuxaq3q6hfV7RqlhMHejov&partner_key=affiliate-123&styles=1,2&colors=red,blue
 */
export function generateEmbedUrl(baseUrl: string, config: EmbedConfig): string {
  const params = new URLSearchParams();
  // Large base64 JSON blobs (`products`, `ap`) go in the URL fragment instead of
  // the query string. Fragments are never sent in the HTTP request, so they don't
  // count against CloudFront's/browsers' request-line length limits — this avoids
  // 414 errors on multi-product configs with many selected styles/fonts/etc.
  const fragmentParams = new URLSearchParams();

  const isMultiProduct = config.products && config.products.length >= 2;

  if (isMultiProduct) {
    // Multi-product path: encode all product configs as a sorted base64 JSON blob.
    // Default product first (index 0), remaining products sorted alphabetically by title.
    const sorted = [...config.products!].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return (a.productTitle ?? '').localeCompare(b.productTitle ?? '');
    });
    fragmentParams.append('products', btoa(JSON.stringify(sorted)));

    // Also include product_tag pointing to the default product so the embed
    // doesn't show a "missing product_tag" error if it encounters an older build.
    const defaultProduct = sorted[0];
    params.append('product_tag', defaultProduct.productTag);
  } else {
    // Single-product path: use flat params (backward compatible with existing URLs).
    params.append('product_tag', config.productTag);

    if (config.shopifyVariantId) {
      params.append('shopify_variant_id', config.shopifyVariantId);
    }

    if (config.shopifyDomain) {
      params.append('shopify_domain', config.shopifyDomain);
    }

    if (config.productTitle) {
      params.append('product_title', config.productTitle);
    }

    if (config.productPriceMin) {
      params.append('price_min', config.productPriceMin);
    }

    if (config.productPriceMax) {
      params.append('price_max', config.productPriceMax);
    }

    if (config.productCurrency) {
      params.append('currency', config.productCurrency);
    }

    if (config.productCompareAtPriceMin) {
      params.append('compare_at_price_min', config.productCompareAtPriceMin);
    }

    if (config.allowedStyles && config.allowedStyles.length > 0) {
      params.append('styles', config.allowedStyles.join(','));
    }

    if (config.allowedColors && config.allowedColors.length > 0) {
      params.append('colors', config.allowedColors.join(','));
    }

    if (config.allowedFonts && config.allowedFonts.length > 0) {
      params.append('fonts', config.allowedFonts.join(','));
    }

    if (config.allowedIcons && config.allowedIcons.length > 0) {
      params.append('icons', config.allowedIcons.join(','));
    }

    if (config.allowedMaterials && config.allowedMaterials.length > 0) {
      params.append('materials', config.allowedMaterials.join(','));
    }
  }

  if (config.partnerId) {
    params.append('partner_id', config.partnerId);
  }

  if (config.partnerKey) {
    params.append('partner_key', config.partnerKey);
    // Use the full Refersion tracking code (e.g. "9011032.2d9ff2") if available,
    // otherwise fall back to the partner key alone.
    params.append('rfsn', config.affiliateRfsn ?? config.partnerKey);
  }

  // Appearance params — encoded as a single base64 JSON blob (ap) for compactness
  // Short keys:
  // ff=fontFamily, fc=customFontClass, br=brandBannerColor, ac=accentColor,
  // pb=primaryButtonColor, pt=primaryButtonText,
  // sb=secondaryButtonColor, st=secondaryButtonText,
  // hb=headerBgColor, bb=bodyBgColor, bd=borderColor,
  // tc=textColor, ts=secondaryTextColor
  const ap: Record<string, string> = {};
  if (config.fontFamily && config.fontFamily !== 'none')
    ap.ff = config.fontFamily;
  if (config.fontFamily === 'custom' && config.customFontClass)
    ap.fc = config.customFontClass;
  if (config.brandBannerColor) ap.br = config.brandBannerColor;
  if (config.accentColor) ap.ac = config.accentColor;
  if (config.primaryButtonColor) ap.pb = config.primaryButtonColor;
  if (config.primaryButtonText) ap.pt = config.primaryButtonText;
  if (config.secondaryButtonColor) ap.sb = config.secondaryButtonColor;
  if (config.secondaryButtonText) ap.st = config.secondaryButtonText;
  if (config.headerBgColor) ap.hb = config.headerBgColor;
  if (config.bodyBgColor) ap.bb = config.bodyBgColor;
  if (config.borderColor) ap.bd = config.borderColor;
  if (config.textColor) ap.tc = config.textColor;
  if (config.secondaryTextColor) ap.ts = config.secondaryTextColor;
  if (config.brandName) ap.bn = config.brandName;
  if (Object.keys(ap).length > 0) {
    fragmentParams.append('ap', btoa(JSON.stringify(ap)));
  }

  // Dashboard-only preview hint — never written to the copy-able snippet.
  if (config.previewProductTag) {
    params.append('preview_product_tag', config.previewProductTag);
  }

  const fragment = fragmentParams.toString();
  return `${baseUrl}?${params.toString()}${fragment ? `#${fragment}` : ''}`;
}

/**
 * Generates HTML iframe snippet code
 *
 * @param embedUrl - The complete embed URL with parameters
 * @param width - Iframe width in pixels (default: "800")
 * @param height - Iframe height in pixels (default: "600")
 * @returns HTML iframe snippet
 *
 * @example
 * const html = generateHtmlSnippet("https://namebubbles.com/embed-config.html?product_tag=xyz");
 * // Returns: <iframe src="https://namebubbles.com/embed-config.html?product_tag=xyz" width="800" height="600"></iframe>
 */
export function generateHtmlSnippet(
  embedUrl: string,
  width?: string,
  height?: string,
  options?: SnippetOptions
): string {
  const finalWidth = width || '800';
  const finalHeight = height || '600';
  const finalEmbedUrl = buildEmbedUrlForSnippet(embedUrl, options);

  if (!options?.mobileAutoResize) {
    return `<iframe src="${finalEmbedUrl}" width="100%" height="${finalHeight}" style="display:block;width:100%;max-width:${finalWidth}px;border:0;" sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation"></iframe>`;
  }

  const embedOrigin = new URL(finalEmbedUrl).origin;
  return `<div style="width:100%;max-width:${finalWidth}px;"><iframe src="${finalEmbedUrl}" width="100%" height="${finalHeight}" style="display:block;width:100%;border:0;" sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation"></iframe><script>(function(){const iframe=document.currentScript.previousElementSibling;function onMessage(event){if(event.origin!=='${embedOrigin}')return;if(event.source!==iframe.contentWindow)return;const data=event.data;if(data&&data.type==='namebubbles:embed-resize'&&typeof data.height==='number'){iframe.style.height=Math.ceil(data.height)+'px';}}window.addEventListener('message',onMessage);})();</script></div>`;
}

/**
 * Generates React component snippet code
 *
 * @param embedUrl - The complete embed URL with parameters
 * @param width - Iframe width in pixels (default: "800")
 * @param height - Iframe height in pixels (default: "600")
 * @returns Complete React functional component
 *
 * @example
 * const component = generateReactSnippet("https://namebubbles.com/embed-config.html?product_tag=xyz");
 * // Returns a complete React component that developers can copy and paste
 */
export function generateReactSnippet(
  embedUrl: string,
  width?: string,
  height?: string,
  options?: SnippetOptions
): string {
  const finalWidth = width || '800';
  const finalHeight = height || '600';
  const finalEmbedUrl = buildEmbedUrlForSnippet(embedUrl, options);

  if (!options?.mobileAutoResize) {
    return `export default function NameBubblesConfigurator() {
  return (
    <iframe
      src="${finalEmbedUrl}"
      width="100%"
      height="${finalHeight}"
      style={{ display: 'block', width: '100%', maxWidth: '${finalWidth}px', border: 'none' }}
      title="NameBubbles Product Configurator"
      sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation"
    />
  );
}`;
  }

  const embedOrigin = new URL(finalEmbedUrl).origin;
  return `import { useEffect, useRef } from 'react';

export default function NameBubblesConfigurator() {
  const iframeRef = useRef(null);

  useEffect(() => {
    function onMessage(event) {
      if (event.origin !== '${embedOrigin}') return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data;
      if (
        data &&
        data.type === 'namebubbles:embed-resize' &&
        typeof data.height === 'number'
      ) {
        iframeRef.current.style.height = \`\${Math.ceil(data.height)}px\`;
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      src="${finalEmbedUrl}"
      width="100%"
      height="${finalHeight}"
      style={{ display: 'block', width: '100%', maxWidth: '${finalWidth}px', border: 'none' }}
      title="NameBubbles Product Configurator"
      sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation"
    />
  );
}`;
}
